import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, IconButton, Tooltip, Paper } from '@mui/material';
import { ZoomIn, ZoomOut, CenterFocusStrong, Fullscreen } from '@mui/icons-material';

interface ZoomPanContainerProps {
  children: React.ReactNode;
  minZoom?: number;
  maxZoom?: number;
  initialZoom?: number;
  onZoomChange?: (zoom: number) => void;
  contentDimensions?: { width: number; height: number };
  boundaryPadding?: number;
}

interface ViewportState {
  zoom: number;
  translateX: number;
  translateY: number;
}

const ZoomPanContainer: React.FC<ZoomPanContainerProps> = ({
  children,
  minZoom = 0.3,
  maxZoom = 2.0,
  initialZoom = 1.0,
  onZoomChange,
  contentDimensions,
  boundaryPadding = 100
}) => {
  const [viewport, setViewport] = useState<ViewportState>({
    zoom: initialZoom,
    translateX: 200, // Start shifted right to show left module
    translateY: 0
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const lastMousePos = useRef({ x: 0, y: 0 });

  // Handle mouse wheel zoom
  const handleWheel = useCallback((event: WheelEvent) => {
    event.preventDefault();
    
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const delta = event.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.max(minZoom, Math.min(maxZoom, viewport.zoom + delta));

    if (newZoom !== viewport.zoom) {
      // Zoom towards mouse position
      const zoomRatio = newZoom / viewport.zoom;
      const newTranslateX = mouseX - (mouseX - viewport.translateX) * zoomRatio;
      const newTranslateY = mouseY - (mouseY - viewport.translateY) * zoomRatio;

      const constrained = constrainTranslation(newTranslateX, newTranslateY);
      setViewport({
        zoom: newZoom,
        translateX: constrained.x,
        translateY: constrained.y
      });

      onZoomChange?.(newZoom);
    }
  }, [viewport, minZoom, maxZoom, onZoomChange]);

  // Handle mouse down for panning
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (event.button === 0) { // Left mouse button
      isDragging.current = true;
      lastMousePos.current = { x: event.clientX, y: event.clientY };
      event.preventDefault();
    }
  }, []);

  // Handle mouse move for panning
  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging.current) return;

    const deltaX = event.clientX - lastMousePos.current.x;
    const deltaY = event.clientY - lastMousePos.current.y;

    setViewport(prev => {
      const newTranslation = constrainTranslation(
        prev.translateX + deltaX,
        prev.translateY + deltaY
      );
      return {
        ...prev,
        translateX: newTranslation.x,
        translateY: newTranslation.y
      };
    });

    lastMousePos.current = { x: event.clientX, y: event.clientY };
  }, []);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Touch events for mobile
  const touchStartPos = useRef<{ x: number; y: number; distance: number } | null>(null);

  // Calculate boundaries based on container size, content size, and zoom
  const calculateBoundaries = useCallback(() => {
    if (!containerRef.current || !contentDimensions) return null;

    const containerRect = containerRef.current.getBoundingClientRect();
    const contentWidth = contentDimensions.width * viewport.zoom;
    const contentHeight = contentDimensions.height * viewport.zoom;

    // Allow 75% of content to go off-screen (keep at least 25% visible)
    const visibleRequirement = 0.25;
    
    // Calculate bounds that ensure at least 25% of content stays visible
    const minX = -contentWidth * (1 - visibleRequirement);
    const maxX = containerRect.width - contentWidth * visibleRequirement;
    
    const minY = -contentHeight * (1 - visibleRequirement);
    const maxY = containerRect.height - contentHeight * visibleRequirement;

    return { minX, maxX, minY, maxY };
  }, [viewport.zoom, contentDimensions]);

  // Constrain translation to boundaries
  const constrainTranslation = useCallback((x: number, y: number) => {
    const boundaries = calculateBoundaries();
    if (!boundaries) return { x, y };

    return {
      x: Math.max(boundaries.minX, Math.min(boundaries.maxX, x)),
      y: Math.max(boundaries.minY, Math.min(boundaries.maxY, y))
    };
  }, [calculateBoundaries]);

  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    if (event.touches.length === 1) {
      // Single touch - start panning
      touchStartPos.current = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
        distance: 0
      };
    } else if (event.touches.length === 2) {
      // Two touches - start pinch zoom
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) + 
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );
      
      touchStartPos.current = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
        distance
      };
    }
  }, []);

  const handleTouchMove = useCallback((event: React.TouchEvent) => {
    event.preventDefault();
    
    if (!touchStartPos.current) return;

    if (event.touches.length === 1 && touchStartPos.current.distance === 0) {
      // Single touch - pan
      const deltaX = event.touches[0].clientX - touchStartPos.current.x;
      const deltaY = event.touches[0].clientY - touchStartPos.current.y;

      setViewport(prev => {
        const newTranslation = constrainTranslation(
          prev.translateX + deltaX,
          prev.translateY + deltaY
        );
        return {
          ...prev,
          translateX: newTranslation.x,
          translateY: newTranslation.y
        };
      });

      touchStartPos.current.x = event.touches[0].clientX;
      touchStartPos.current.y = event.touches[0].clientY;
    } else if (event.touches.length === 2) {
      // Two touches - pinch zoom
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      const distance = Math.sqrt(
        Math.pow(touch2.clientX - touch1.clientX, 2) + 
        Math.pow(touch2.clientY - touch1.clientY, 2)
      );

      const scale = distance / touchStartPos.current.distance;
      const newZoom = Math.max(minZoom, Math.min(maxZoom, viewport.zoom * scale));

      if (newZoom !== viewport.zoom) {
        setViewport(prev => ({
          ...prev,
          zoom: newZoom
        }));
        
        onZoomChange?.(newZoom);
        touchStartPos.current.distance = distance;
      }
    }
  }, [viewport.zoom, minZoom, maxZoom, onZoomChange]);

  const handleTouchEnd = useCallback(() => {
    touchStartPos.current = null;
  }, []);

  // Zoom control functions
  const zoomIn = useCallback(() => {
    const newZoom = Math.min(maxZoom, viewport.zoom + 0.2);
    
    if (!containerRef.current || !contentDimensions) {
      setViewport(prev => ({ ...prev, zoom: newZoom }));
      onZoomChange?.(newZoom);
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const contentWidth = contentDimensions.width * newZoom;
    const contentHeight = contentDimensions.height * newZoom;

    // Calculate boundaries for new zoom level (keep at least 25% visible)
    const visibleRequirement = 0.25;
    const minX = -contentWidth * (1 - visibleRequirement);
    const maxX = containerRect.width - contentWidth * visibleRequirement;
    const minY = -contentHeight * (1 - visibleRequirement);
    const maxY = containerRect.height - contentHeight * visibleRequirement;

    setViewport(prev => ({
      zoom: newZoom,
      translateX: Math.max(minX, Math.min(maxX, prev.translateX)),
      translateY: Math.max(minY, Math.min(maxY, prev.translateY))
    }));
    
    onZoomChange?.(newZoom);
  }, [viewport.zoom, maxZoom, onZoomChange, contentDimensions, boundaryPadding]);

  const zoomOut = useCallback(() => {
    const newZoom = Math.max(minZoom, viewport.zoom - 0.2);
    
    if (!containerRef.current || !contentDimensions) {
      setViewport(prev => ({ ...prev, zoom: newZoom }));
      onZoomChange?.(newZoom);
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const contentWidth = contentDimensions.width * newZoom;
    const contentHeight = contentDimensions.height * newZoom;

    // Calculate boundaries for new zoom level (keep at least 25% visible)
    const visibleRequirement = 0.25;
    const minX = -contentWidth * (1 - visibleRequirement);
    const maxX = containerRect.width - contentWidth * visibleRequirement;
    const minY = -contentHeight * (1 - visibleRequirement);
    const maxY = containerRect.height - contentHeight * visibleRequirement;

    setViewport(prev => ({
      zoom: newZoom,
      translateX: Math.max(minX, Math.min(maxX, prev.translateX)),
      translateY: Math.max(minY, Math.min(maxY, prev.translateY))
    }));
    
    onZoomChange?.(newZoom);
  }, [viewport.zoom, minZoom, onZoomChange, contentDimensions, boundaryPadding]);

  const resetView = useCallback(() => {
    const constrained = constrainTranslation(200, 0); // Reset to initial right-shifted position
    setViewport({
      zoom: 1.0,
      translateX: constrained.x,
      translateY: constrained.y
    });
    onZoomChange?.(1.0);
  }, [onZoomChange, constrainTranslation]);

  const fitToScreen = useCallback(() => {
    if (!containerRef.current || !contentRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();

    // Calculate scale to fit content in container with some padding
    const padding = 50; // 50px padding
    const scaleX = (containerRect.width - padding * 2) / contentRect.width;
    const scaleY = (containerRect.height - padding * 2) / contentRect.height;
    const scale = Math.min(scaleX, scaleY, maxZoom);

    // Center the content
    const centerX = (containerRect.width - contentRect.width * scale) / 2;
    const centerY = (containerRect.height - contentRect.height * scale) / 2;

    const constrained = constrainTranslation(centerX, centerY);
    setViewport({
      zoom: scale,
      translateX: constrained.x,
      translateY: constrained.y
    });

    onZoomChange?.(scale);
  }, [maxZoom, onZoomChange]);

  // Add event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('wheel', handleWheel);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleWheel, handleMouseMove, handleMouseUp]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        cursor: isDragging.current ? 'grabbing' : 'grab',
        userSelect: 'none'
      }}
    >
      {/* Zoom Controls */}
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)'
        }}
      >
        <Tooltip title="Zoom In" placement="left">
          <IconButton onClick={zoomIn} disabled={viewport.zoom >= maxZoom}>
            <ZoomIn />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out" placement="left">
          <IconButton onClick={zoomOut} disabled={viewport.zoom <= minZoom}>
            <ZoomOut />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset View" placement="left">
          <IconButton onClick={resetView}>
            <CenterFocusStrong />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fit to Screen" placement="left">
          <IconButton onClick={fitToScreen}>
            <Fullscreen />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Zoom Level Indicator */}
      <Paper
        elevation={2}
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          zIndex: 10,
          px: 2,
          py: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          fontSize: '0.875rem',
          fontWeight: 'medium'
        }}
      >
        {Math.round(viewport.zoom * 100)}%
      </Paper>

      {/* Container for zoom/pan */}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Content with transform applied */}
        <Box
          ref={contentRef}
          sx={{
            transform: `translate(${viewport.translateX}px, ${viewport.translateY}px) scale(${viewport.zoom})`,
            transformOrigin: '0 0',
            transition: isDragging.current ? 'none' : 'transform 0.1s ease-out',
            willChange: 'transform'
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default ZoomPanContainer;